
import { Event, OccasionResponse } from "../../lib/types";
import { get, post, patch, del } from '../apiService';
import { UserRoles } from '@/contexts/auth/enums';
import {
  CreateEventRequest,
  EventStatus,
  EventsQueryParams,
  HydraCollectionResponse
} from './eventTypes';

export const fetchAllPublicEvents = async (params?: EventsQueryParams): Promise<Event[]> => {
  try {
    console.log('🌍 Fetching ALL public events (calendar view)');
    // Construct query parameters if provided
    let queryParams = '';
    const urlParams = new URLSearchParams();

    // Never add owned_by_me filter - we want ALL public events
    
    if (params) {
      if (params.page) urlParams.append('page', params.page.toString());
      if (params.itemsPerPage) urlParams.append('itemsPerPage', params.itemsPerPage.toString());
      if (params.search) urlParams.append('search', params.search);
      if (params.type) urlParams.append('type', params.type);
      if (params.audience) urlParams.append('audience', params.audience);
      if (params.dateFrom) urlParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) urlParams.append('dateTo', params.dateTo);
    }
    queryParams = urlParams.toString();

    const endpoint = queryParams ? `/occasions?${queryParams}` : '/occasions';
    console.log('🌐 Fetching from endpoint:', endpoint);
    const response = await get<HydraCollectionResponse<OccasionResponse>>(endpoint);
    console.log('📡 API Response:', response);
    console.log('📦 API Data structure:', response.data);
    console.log('👥 Hydra members:', response.data['hydra:member']?.length || 0, 'items');

    // convert data to Event[] type
    const rawEvents = response.data['hydra:member'] || [];
    console.log('🔍 Filtering events with dateStart and dateEnd...');
    const filteredEvents = rawEvents.filter((event: OccasionResponse) => {
      const hasStartDate = !!event.dateStart;
      const hasEndDate = !!event.dateEnd;
      if (!hasStartDate) console.log('⚠️ Event missing dateStart:', event['@id'], event.name);
      if (!hasEndDate) console.log('⚠️ Event missing dateEnd:', event['@id'], event.name);
      return hasStartDate && hasEndDate;
    });
    console.log('✂️ Filtered events:', filteredEvents.length, 'out of', rawEvents.length, 'total');
    
    return filteredEvents.map((event: OccasionResponse) => ({
        ...event,
        id: event['@id'], // Use the full IRI as the ID
        code: event.code || event.id,  // Keep code for backward compatibility
        professions: event.professions || [], // Ensure professions is present
        targetAudience: [], // Empty array for compatibility
        venue: event.venue || { city: '', name: '', address: '' }, // Default empty EventVenue object
        description: event.description || '', // Ensure description is present
      }));
  } catch (error) {
    console.error('Error fetching all public events:', error);
    throw error;
  }
};

export const fetchMyEvents = async (userRole?: UserRoles, params?: EventsQueryParams): Promise<Event[]> => {
  try {
    console.log('🔑 User role:', userRole);
    // Construct query parameters if provided
    let queryParams = '';
    const urlParams = new URLSearchParams();

    // Only add owned_by_me=true if the user is an organizer
    if (userRole === UserRoles.ORGANIZER) {
      console.log('👤 Adding owned_by_me=true for organizer');
      urlParams.append('owned_by_me', 'true');
    } else {
      console.log('🌍 Fetching all public events (no user role filter)');
    }

    if (params) {
      if (params.page) urlParams.append('page', params.page.toString());
      if (params.itemsPerPage) urlParams.append('itemsPerPage', params.itemsPerPage.toString());
      if (params.search) urlParams.append('search', params.search);
      if (params.type) urlParams.append('type', params.type);
      if (params.audience) urlParams.append('audience', params.audience);
      if (params.dateFrom) urlParams.append('dateFrom', params.dateFrom);
      if (params.dateTo) urlParams.append('dateTo', params.dateTo);
    }
    queryParams = urlParams.toString();

    const endpoint = queryParams ? `/occasions?${queryParams}` : '/occasions';
    console.log('🌐 Fetching from endpoint:', endpoint);
    const response = await get<HydraCollectionResponse<OccasionResponse>>(endpoint);
    console.log('📡 API Response:', response);
    console.log('📦 API Data structure:', response.data);
    console.log('👥 Hydra members:', response.data['hydra:member']?.length || 0, 'items');

    // convert data to Event[] type
    const rawEvents = response.data['hydra:member'] || [];
    console.log('🔍 Filtering events with dateStart and dateEnd...');
    const filteredEvents = rawEvents.filter((event: OccasionResponse) => {
      const hasStartDate = !!event.dateStart;
      const hasEndDate = !!event.dateEnd;
      if (!hasStartDate) console.log('⚠️ Event missing dateStart:', event['@id'], event.name);
      if (!hasEndDate) console.log('⚠️ Event missing dateEnd:', event['@id'], event.name);
      return hasStartDate && hasEndDate;
    });
    console.log('✂️ Filtered events:', filteredEvents.length, 'out of', rawEvents.length, 'total');
    
    return filteredEvents.map((event: OccasionResponse) => ({
        ...event,
        id: event['@id'], // Use the full IRI as the ID
        code: event.code || event.id,  // Keep code for backward compatibility
        professions: event.professions || [], // Ensure professions is present
        targetAudience: [], // Empty array for compatibility
        venue: event.venue || { city: '', name: '', address: '' }, // Default empty EventVenue object
        description: event.description || '', // Ensure description is present
      }));
  } catch (error) {
    console.error('Error fetching my events:', error);
    throw error;
  }
};

export const createEvent = async (eventData: CreateEventRequest): Promise<Event> => {
  try {
    console.log("Creating event with data:", eventData);
    // Only set default status if not provided in the request
    if (!eventData.status) {
      eventData.status = EventStatus.DRAFT;
    }
    const response = await post<Event>('/occasions', eventData);
    return response.data;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

export const fetchEvent = async (slugOrCode: string | null): Promise<Event | null> => {
  try {
    if (!slugOrCode) {
      throw new Error('Event identifier (slug or code) is required');
    }

    // Use the IRI directly if it starts with '/', otherwise use slug/code
    let endpoint;
    if (slugOrCode.startsWith('/')) {
      endpoint = slugOrCode;
    } else {
      // First try to find by slug
      try {
        const slugResponse = await get<HydraCollectionResponse<OccasionResponse>>(`/occasions?slug=${slugOrCode}`);
        if (slugResponse.data['hydra:member'] && slugResponse.data['hydra:member'].length > 0) {
          const event = slugResponse.data['hydra:member'][0];
          return {
            ...event,
            id: event['@id'],
            code: event.code || event.id,
            professions: event.professions || [],
            venue: event.venue || { city: '', name: '', address: '' },
            description: event.description || '',
          };
        }
      } catch (error) {
        console.warn('No event found by slug, trying code next:', error);
      }

      // If not found by slug, try code
      try {
        endpoint = `/occasions/${slugOrCode}`;
        const response = await get<Event>(endpoint);
        return response.data;
      } catch (error) {
        console.warn('No event found by code either:', error);
        return null;
      }
    }

    const response = await get<Event>(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error fetching event:', error);
    return null;
  }
};

export const updateEvent = async (eventIri: string, eventData: Partial<CreateEventRequest>): Promise<Event> => {
  try {
    console.log("Updating event with data:", eventData);
    // Only set default status if not provided in the request
    if (!eventData.status) {
      eventData.status = EventStatus.DRAFT;
    }
    const response = await patch<Event>(eventIri, eventData);
    return response.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventIri: string): Promise<void> => {
  try {
    // Use the IRI directly for the delete endpoint
    await del<void>(eventIri);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};
